"use client"

import * as React from "react"
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Gauge,
  Loader2,
  Save,
  Shield,
  Sparkles,
} from "lucide-react"

import { api, type Effort, type Risk, type Skill, type WorkflowSpec } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { WorkflowFlow } from "@/components/workflow-flow"
import { SkillPicker } from "@/components/skill-picker"

const EFFORT_INFO: Record<Effort, { label: string; hint: string }> = {
  fast: { label: "Fast", hint: "Haiku · quick, simple plans" },
  balanced: { label: "Balanced", hint: "Sonnet · the default" },
  deep: { label: "Deep", hint: "Opus · multi-turn, complex plans" },
}

type Stage = "setup" | "prompt" | "review"

/** Starting points, so nobody faces an empty box. */
const EXAMPLES: { title: string; prompt: string; skills: string[] }[] = [
  {
    title: "Nightly multi-sport simulation",
    prompt:
      "run simulation of different sports each 1-2 videos, between 6pm-7pm IST time",
    skills: ["fsp.app_user", "fsp.read"],
  },
  {
    title: "Queue health check",
    prompt:
      "every 30 minutes read the scoring queue and the on-duty verifiers, then have the assistant summarise whether the queue is at risk",
    skills: ["fsp.read"],
  },
  {
    title: "Fill the queue for training",
    prompt:
      "submit 5 squash sessions one after another with a 20 second pause between each, so verifiers have work to practise on",
    skills: ["fsp.app_user"],
  },
]

/** Drafts survive a reload — losing a half-written plan to a refresh is rude. */
const DRAFT_KEY = "workflows-workflow-draft"

type Draft = {
  name: string
  context: string
  selected: string[]
  effort: Effort
  prompt: string
}

export function WorkflowBuilder({
  skills,
  onSaved,
  onCancel,
}: {
  skills: Skill[]
  onSaved: () => void
  onCancel: () => void
}) {
  const { user } = useAuth()

  const [stage, setStage] = React.useState<Stage>("setup")
  const [name, setName] = React.useState("")
  const [context, setContext] = React.useState("")
  const [selected, setSelected] = React.useState<string[]>([])
  const [effort, setEffort] = React.useState<Effort>("balanced")
  const [prompt, setPrompt] = React.useState("")

  const [busy, setBusy] = React.useState(false)
  const [restored, setRestored] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [specs, setSpecs] = React.useState<WorkflowSpec[]>([])
  const [reasoning, setReasoning] = React.useState("")
  const [risks, setRisks] = React.useState<Risk[]>([])
  const [cost, setCost] = React.useState<number | null>(null)
  const [acknowledged, setAcknowledged] = React.useState(false)

  // Restore a draft once, on mount.
  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY)
      if (!raw) return
      const d = JSON.parse(raw) as Draft
      if (d.name) setName(d.name)
      if (d.context) setContext(d.context)
      if (d.selected?.length) setSelected(d.selected)
      if (d.effort) setEffort(d.effort)
      if (d.prompt) setPrompt(d.prompt)
      if (d.name || d.prompt) setRestored(true)
    } catch {
      // A corrupt or blocked store is not worth failing the form over.
    }
  }, [])

  // Persist on every change.
  React.useEffect(() => {
    try {
      const d: Draft = { name, context, selected, effort, prompt }
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(d))
    } catch {
      /* private mode */
    }
  }, [name, context, selected, effort, prompt])

  const clearDraft = React.useCallback(() => {
    try {
      window.localStorage.removeItem(DRAFT_KEY)
    } catch {
      /* ignore */
    }
  }, [])

  // System skills (the AI assistant) are always available and never chosen.
  const selectable = skills.filter((w) => !w.system)
  const systemSkills = skills.filter((w) => w.system)

  const blocking = risks.filter((r) => r.level === "blocking")
  const advisory = risks.filter((r) => r.level === "advisory")

  const promptRef = React.useRef<HTMLTextAreaElement>(null)

  // ── @mention autocomplete ──
  // Watches the token immediately before the caret. If it starts with "@",
  // the matching skills are offered; Enter/Tab completes, Escape dismisses.
  const [mentionQuery, setMentionQuery] = React.useState<string | null>(null)
  const [mentionIndex, setMentionIndex] = React.useState(0)

  const mentionable = React.useMemo(
    () => skills.filter((w) => w.system || selected.includes(w.id)),
    [skills, selected],
  )

  const matches = React.useMemo(() => {
    if (mentionQuery === null) return []
    const q = mentionQuery.toLowerCase()
    return mentionable
      .filter((w) => w.id.toLowerCase().includes(q) || w.title.toLowerCase().includes(q))
      .slice(0, 6)
  }, [mentionQuery, mentionable])

  const onPromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setPrompt(value)
    const upto = value.slice(0, e.target.selectionStart ?? value.length)
    const m = /(?:^|\s)@([\w.]*)$/.exec(upto)
    setMentionQuery(m ? (m[1] ?? "") : null)
    setMentionIndex(0)
  }

  const applyMention = (w: Skill) => {
    const el = promptRef.current
    if (!el) return
    const caret = el.selectionStart ?? prompt.length
    const before = prompt.slice(0, caret)
    const replaced = before.replace(/(?:^|\s)@([\w.]*)$/, (full) =>
      full.startsWith("@") ? `@${w.id} ` : `${full[0]}@${w.id} `,
    )
    const next = replaced + prompt.slice(caret)
    setPrompt(next)
    setMentionQuery(null)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(replaced.length, replaced.length)
    })
  }

  const onPromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery === null || matches.length === 0) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setMentionIndex((i) => (i + 1) % matches.length)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setMentionIndex((i) => (i - 1 + matches.length) % matches.length)
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault()
      applyMention(matches[mentionIndex]!)
    } else if (e.key === "Escape") {
      setMentionQuery(null)
    }
  }

  /** Inserts a @skill mention at the caret. */
  const mention = (w: Skill) => {
    const el = promptRef.current
    const tag = `@${w.id} `
    if (!el) return setPrompt((p) => p + tag)
    const at = el.selectionStart ?? prompt.length
    setPrompt(prompt.slice(0, at) + tag + prompt.slice(at))
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(at + tag.length, at + tag.length)
    })
  }

  const generate = async () => {
    setBusy(true)
    setError(null)
    const res = await api.generate({
      prompt,
      context: [name && `Workflow name: ${name}`, context].filter(Boolean).join("\n"),
      allowedSkills: selected,
      effort,
    })
    if (res.error) setError(res.error)
    else {
      // Defend against an unexpected shape: mapping over undefined threw and
      // left the review screen blank with no explanation.
      const generated = res.data?.workflows
      if (!Array.isArray(generated) || generated.length === 0) {
        setError(
          "The planner returned nothing usable. If the API was just restarted, try again.",
        )
        setBusy(false)
        return
      }
      setSpecs(generated.map((w) => w.spec))
      setReasoning(res.data!.reasoning)
      setRisks(res.data!.risks)
      setCost(res.data!.costUsd)
      setAcknowledged(false)
      setStage("review")
    }
    setBusy(false)
  }

  const save = async () => {
    if (!specs.length) return
    setBusy(true)
    setError(null)
    const res = await api.saveWorkflow({
      prompt,
      // Saved together — half of a two-trigger pair would leave a workflow
      // with nothing able to trigger it.
      specs,
      createdBy: user?.email ?? undefined,
      acknowledgeRisks: acknowledged,
    })
    if (res.error) setError(res.error)
    else {
      clearDraft()
      onSaved()
    }
    setBusy(false)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onCancel} className="gap-1.5">
          <ArrowLeft className="size-3.5" />
          Cancel
        </Button>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {(["setup", "prompt", "review"] as Stage[]).map((s, i) => (
            <React.Fragment key={s}>
              {i > 0 && <span className="text-muted-foreground/40">›</span>}
              <span className={stage === s ? "font-medium text-foreground" : ""}>
                {s === "setup" ? "Setup" : s === "prompt" ? "Describe" : "Review"}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {error && (
        <Card className="border-destructive/40">
          <CardContent className="py-3">
            <p className="font-mono text-xs text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* ── 1. Setup — name and context only. Asking for skills and model
             here buried the two things that actually need thought. ─────── */}
      {stage === "setup" && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Name this workflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {restored && (
              <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/40 px-3 py-2">
                <p className="text-xs text-muted-foreground">Restored an unsaved draft.</p>
                <button
                  type="button"
                  onClick={() => {
                    clearDraft()
                    setName("")
                    setContext("")
                    setSelected([])
                    setPrompt("")
                    setRestored(false)
                  }}
                  className="text-xs text-muted-foreground underline-offset-4 hover:underline"
                >
                  Start fresh
                </button>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Start from an example</Label>
              <div className="grid gap-1.5">
                {EXAMPLES.map((ex) => (
                  <button
                    key={ex.title}
                    type="button"
                    onClick={() => {
                      setName(ex.title)
                      setPrompt(ex.prompt)
                      setSelected(ex.skills.filter((id) => selectable.some((w) => w.id === id)))
                    }}
                    className="rounded-md border px-3 py-2 text-left transition-colors hover:bg-muted/40"
                  >
                    <p className="text-sm">{ex.title}</p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">{ex.prompt}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wf-name" className="text-sm">
                Name
              </Label>
              <Input
                id="wf-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nightly multi-sport simulation"
                className="px-3.5 text-sm md:text-sm"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wf-context" className="text-sm">
                Context <span className="font-normal text-muted-foreground">optional</span>
              </Label>
              <textarea
                id="wf-context"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                rows={3}
                placeholder="Anything the planner should always know — constraints, conventions, what this is for."
                className="w-full resize-y rounded-md border border-input bg-transparent px-3.5 py-2.5 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50"
              />
            </div>

            <Button size="lg" className="w-full" onClick={() => setStage("prompt")} disabled={!name.trim()}>
              Continue
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── 2. Describe — the prompt, with the tools it may use right
             beside it, and effort tucked into the footer. ──────────────── */}
      {stage === "prompt" && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">What should it do?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <textarea
                ref={promptRef}
                value={prompt}
                onChange={onPromptChange}
                onKeyDown={onPromptKeyDown}
                rows={5}
                placeholder="run simulation of different sports each 1-2 videos, between 6pm-7pm IST — type @ to reference a skill"
                className="w-full resize-y rounded-md border border-input bg-transparent px-3.5 py-2.5 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50"
                autoFocus
              />
              {mentionQuery !== null && matches.length > 0 && (
                <div className="absolute left-3 top-full z-20 mt-1 w-72 overflow-hidden rounded-md border bg-popover shadow-lg">
                  {matches.map((w, i) => (
                    <button
                      key={w.id}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        applyMention(w)
                      }}
                      onMouseEnter={() => setMentionIndex(i)}
                      className={
                        "flex w-full items-center gap-2 px-3 py-2 text-left " +
                        (i === mentionIndex ? "bg-muted" : "")
                      }
                    >
                      <span className="font-mono text-xs text-muted-foreground">@{w.id}</span>
                      <span className="truncate text-sm">{w.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Skills it may use</Label>
              <SkillPicker skills={selectable} selected={selected} onChange={setSelected} />
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                <span className="text-[11px] text-muted-foreground">Mention:</span>
                {skills
                  .filter((w) => w.system || selected.includes(w.id))
                  .map((w) => (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => mention(w)}
                      className="rounded border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                    >
                      @{w.id}
                    </button>
                  ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Gauge className="size-3.5" />
                Thinking
                <select
                  value={effort}
                  onChange={(e) => setEffort(e.target.value as Effort)}
                  className="rounded-md border border-input bg-transparent px-2 py-1 text-xs outline-none focus-visible:border-ring"
                >
                  {(Object.keys(EFFORT_INFO) as Effort[]).map((e) => (
                    <option key={e} value={e}>
                      {EFFORT_INFO[e].label}
                    </option>
                  ))}
                </select>
                <span className="hidden sm:inline">{EFFORT_INFO[effort].hint}</span>
              </label>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStage("setup")}>
                  Back
                </Button>
                <Button
                  className="gap-2"
                  onClick={() => void generate()}
                  disabled={busy || !prompt.trim() || selected.length === 0}
                >
                  {busy ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                  {busy ? "Thinking…" : "Generate plan"}
                </Button>
              </div>
            </div>
            {selected.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Pick at least one skill — the planner can only use what you allow.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── 3. Review ──────────────────────────────────────────────────── */}
      {stage === "review" && specs.length > 0 && (
        <div className="space-y-4">
          {reasoning && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Sparkles className="size-4 text-primary" />
                  How it will work
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-muted-foreground">{reasoning}</p>
                {cost !== null && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Planning cost ${cost.toFixed(4)}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {specs.length > 1 && (
            <p className="text-sm text-muted-foreground">
              This needs {specs.length} workflows — a workflow has one trigger, and this
              request has {specs.length}. They save together.
            </p>
          )}
          {specs.map((sp, i) => (
            <div key={i} className="space-y-2">
              {specs.length > 1 && (
                <p className="text-sm font-medium">
                  {i + 1}. {sp.name}
                </p>
              )}
              <WorkflowFlow spec={sp} height={420} />
            </div>
          ))}

          {risks.length > 0 && (
            <Card className={blocking.length ? "border-destructive/50" : ""}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Shield className="size-4" />
                  {blocking.length ? "Needs your approval" : "Worth knowing"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[...blocking, ...advisory].map((r, i) => (
                  <div key={i} className="flex gap-2.5">
                    <AlertTriangle
                      className={
                        "mt-0.5 size-4 shrink-0 " +
                        (r.level === "blocking" ? "text-destructive" : "text-muted-foreground")
                      }
                    />
                    <div>
                      <p className="text-sm font-medium">{r.title}</p>
                      <p className="text-xs leading-relaxed text-muted-foreground">{r.detail}</p>
                    </div>
                  </div>
                ))}

                {blocking.length > 0 && (
                  <button
                    onClick={() => setAcknowledged((a) => !a)}
                    className="flex w-full items-start gap-2.5 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-left"
                  >
                    <span
                      className={
                        "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border " +
                        (acknowledged ? "border-destructive bg-destructive" : "border-destructive/60")
                      }
                    >
                      {acknowledged && <Check className="size-2.5 text-white" />}
                    </span>
                    <span className="text-xs leading-relaxed">
                      I understand what this does and want to save it anyway.
                    </span>
                  </button>
                )}
              </CardContent>
            </Card>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setStage("prompt")}>
              Edit prompt
            </Button>
            <Button
              className="gap-2"
              onClick={() => void save()}
              disabled={busy || (blocking.length > 0 && !acknowledged)}
            >
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              Save {specs.length > 1 ? `${specs.length} workflows` : "workflow"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Saves stopped and in test mode. You turn it on and go live explicitly.
          </p>
        </div>
      )}
    </div>
  )
}
